import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quoteService } from '../../services/quoteService';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

const QuotesPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, quote: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchQuotes = async () => {
      if (!token) {
        setIsLoading(false);
        setError('Authentication required.');
        return;
      }
      try {
        setIsLoading(true);
        const response = await fetch('/api/quotes', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || 'Failed to fetch quotes');
        }
        const data = await response.json();
        if (data.status === 'success') {
          setQuotes(data.data.quotes || []);
        } else {
          throw new Error(data.message || 'Could not parse quotes data');
        }
      } catch (err) {
        console.error('Error fetching quotes:', err);
        setError(err.message || 'Failed to load quotes.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotes();
  }, [token]);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleDeleteClick = (quote) => {
    setDeleteModal({ isOpen: true, quote });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.quote) return;
    
    setIsDeleting(true);
    try {
      await quoteService.deleteQuote(deleteModal.quote.id);
      setQuotes(prev => prev.filter(quote => quote.id !== deleteModal.quote.id));
      setDeleteModal({ isOpen: false, quote: null });
    } catch (err) {
      console.error('Error deleting quote:', err);
      setError(err.message || 'Failed to delete quote');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, quote: null });
  };

  // Check if user has permission to delete
  const canDelete = user && ['admin', 'manager'].includes(user.role);

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-brand-text-dark">Quotes Management</h1>
          <p className="text-lg text-gray-600 mt-2">
            View, create, and manage customer quotes.
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/quotes/new')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-150 ease-in-out"
        >
          + Create New Quote
        </button>
      </header>

      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <p className="text-xl text-gray-500">Loading quotes...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {!isLoading && !error && (
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            {quotes.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500 text-lg">No quotes found.</p>
                <p className="text-gray-400 mt-2">Why not create one now?</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote #</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                        <Link to={`/admin/quotes/view/${quote.id}`}>{quote.quoteNumber}</Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{quote.client?.username || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(quote.quoteDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(quote.expiryDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${parseFloat(quote.totalAmount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ 
                            quote.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                            quote.status === 'sent_to_client' ? 'bg-blue-100 text-blue-800' :
                            quote.status === 'accepted_by_client' ? 'bg-green-100 text-green-800' :
                            quote.status === 'rejected_by_client' ? 'bg-red-100 text-red-800' :
                            quote.status === 'converted_to_estimate' ? 'bg-teal-100 text-teal-800' :
                            quote.status === 'converted_to_invoice' ? 'bg-purple-100 text-purple-800' :
                            quote.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                            'bg-gray-200 text-gray-700'
                        }`}>
                          {quote.status ? quote.status.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN'}
                        </span>
                        {quote.status === 'converted_to_estimate' && quote.convertedEstimateId && (
                          <Link to={`/admin/estimates/view/${quote.convertedEstimateId}`} className="ml-2 text-xs text-teal-600 hover:text-teal-800">
                            (View Estimate)
                          </Link>
                        )}
                        {quote.status === 'converted_to_invoice' && quote.convertedInvoiceId && (
                          <Link to={`/admin/invoices/view/${quote.convertedInvoiceId}`} className="ml-2 text-xs text-purple-600 hover:text-purple-800">
                            (View Invoice)
                          </Link>
                        )}
                      </td>                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                        <Link to={`/admin/quotes/view/${quote.id}`} className="text-indigo-600 hover:text-indigo-900 mr-3">View</Link>
                        <Link to={`/admin/quotes/edit/${quote.id}`} className="text-yellow-600 hover:text-yellow-900 mr-3">Edit</Link>
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteClick(quote)}
                            className="text-red-600 hover:text-red-900"
                            disabled={isDeleting}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Quote"
        message={`Are you sure you want to delete quote ${deleteModal.quote?.quoteNumber || deleteModal.quote?.id}? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default QuotesPage; 